import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { RootState } from "../../../../../store/store";
import { GetBillingTransactions } from "../../../../../store/user/policyDetails/policyDetailsActions";
import { Timeline, timelineOppositeContentClasses } from "@mui/lab";
import CustomTimeline from "../../../../../common/CustomTimeline/CustomTimeline";
import moment from "moment";
import { dateFormat } from "../../../../../constants/Constants";
import BillingActivityCard from "./BillingActivityCard/BillingActivityCard";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";
import ActivityFilter from "../../../../../common/ActivityFilter/ActivityFilter";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "../../../../../common/CustomSkeleton/CustomSkeleton";
import { ActivityStyles } from "../ActivityStyles";

const Billing = () => {
  const dispatch = useAppDispatch();

  const { carrier, system, lobCode } = useParams();

  const { policyDetailsHeader, billingTransactions } = useAppSelector(
    (state: RootState) => state.policyDetails
  );
  const { calledReducerType, isLoading } = useAppSelector(
    (state: RootState) => state.common
  );

  const { t } = useTranslation();

  const [selectedBillingTerm, setSelectedBillingTerm] = useState<string | null>(
    null
  );

  const isMobile = useMediaQuery("(max-width:1024px)");

  useEffect(() => {
    if (
      policyDetailsHeader &&
      policyDetailsHeader.accountId &&
      carrier &&
      system &&
      lobCode
    ) {
      dispatch(
        GetBillingTransactions({
          accountId: policyDetailsHeader?.accountId,
          carrier,
          system,
          lobCode,
        })
      );
    }
  }, [dispatch, policyDetailsHeader, carrier, system, lobCode]);

  const uniquePolicyTerms = billingTransactions
    ? Array.from(
        new Set(
          billingTransactions.map((transaction) => transaction.policyTerm)
        )
      )
    : [];

  const filteredTransactions = selectedBillingTerm
    ? billingTransactions.filter(
        (transaction) => transaction.policyTerm === selectedBillingTerm
      )
    : billingTransactions;

  return (
    <Box 
      id="billing_activity-container"
      data-test-id="test-billing_activity-container"
      aria-label="Billing Activity Container"
    >
      <ActivityFilter
        items={uniquePolicyTerms}
        onFilterChange={setSelectedBillingTerm}
        label="Policy Term"
      />
      {isLoading &&
      calledReducerType === "policyDetails/GetBillingTransactions" ? (
        <CustomSkeleton count={3} direction="column" height="120px" />
      ) : filteredTransactions?.length > 0 ? (
        <Timeline
          id="billing_activity-timeline"
          data-test-id="test-billing_activity-timeline"
          aria-label="Billing Activity Timeline"
          sx={{
            padding: "0px",
            [`& .${timelineOppositeContentClasses.root}`]: { flex: 0.1 },
          }}
        >
          {[...filteredTransactions]
            .sort((a, b) => {
              const dateA = new Date(a.transactionDate).getTime();
              const dateB = new Date(b.transactionDate).getTime();
              return dateB - dateA;
            })
            .map((transaction, index) => (
              <CustomTimeline
                key={`policy-activity-${index}`}
                oppositeContent={moment(transaction.transactionDate).format(
                  dateFormat
                )}
                oppositeContentStyle={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                dotStyle={{
                  display: "flex",
                  alignItems: "center",
                }}
                children={
                  <BillingActivityCard
                    transaction={transaction}
                    isLast={
                      index ===
                      (billingTransactions
                        ? isMobile
                          ? billingTransactions.length - 2
                          : billingTransactions.length - 1
                        : false)
                    }
                  />
                }
              />
            ))}
        </Timeline>
      ) : (
        <Box 
          id="billing_activity-no-data"
          data-test-id="test-billing_activity-no-data"
          aria-label="No Billing Data Available"
          sx={ActivityStyles.noDataAvailable}
        >
          <Typography 
            id="billing_activity-no-data-text"
            data-test-id="test-billing_activity-no-data-text"
            aria-label="No Billing Data Text"
            variant="h6" 
            fontSize="1rem"
          >
            {t("No Transactions Available")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Billing;
